'use client';

import {type CategoryClass} from '#/cms/schemas/category-class.schema';
import {type Category} from '#/cms/schemas/category.schema';
import {type PepIconName} from '@calvium/pep-icons';
import classNames from 'classnames';
import {useId, useMemo} from 'react';
import {useUiStrings} from '../store/uiStrings';
import Box from './Box';
import {IconButton} from './IconButton';
import {PepIcon} from './PepIcon';
import {PopoverTooltip} from './PopoverTooltip';
import {Heading3} from './Text';

type CategoryBoxSectionProps<_Category extends Category> = {
  title?: string;
  pepIcon?: PepIconName;
  categories: _Category[];
  activeCategoryIds: _Category['id'][];
  disabledCategoryIds?: _Category['id'][];
  handleFilterChange: (change: Record<string, boolean>) => void;
  className?: string;
};

export const CategoryBoxSection = <_Category extends Category>(props: CategoryBoxSectionProps<_Category>) => {
  const {title, pepIcon, categories, activeCategoryIds, disabledCategoryIds, handleFilterChange, className} = props;
  const id = useId();
  return (
    <div className={classNames('flex flex-col gap-y-3', className)}>
      {!!title && (
        <div className="flex flex-row items-center gap-x-2">
          {!!pepIcon && (
            <div className="rounded-full bg-primary p-2">
              <PepIcon iconName={pepIcon} strokeColor="light" />
            </div>
          )}
          {!!title && <p className="line-clamp-1 text-small font-bold leading-none">{title}</p>}
        </div>
      )}
      <div className="flex flex-row flex-wrap gap-2">
        {categories.map(category => (
          <IconButton
            key={`${id}-category-${category.id}`}
            pepIcon={category.pepIcon ?? 'NavigationMenuHorizontal'}
            label={category.title ?? ''}
            active={activeCategoryIds.includes(category.id)}
            disabled={disabledCategoryIds?.includes(category.id)}
            iconHoverColor="secondary"
            onClick={() => handleFilterChange({[category.id]: !activeCategoryIds.includes(category.id)})}
          />
        ))}
      </div>
    </div>
  );
};

type CategoryBoxProps<_Category extends Category> = {
  title: string;
  tooltipWithEmphasis?: string;
  categoryClasses: CategoryClass[];
  categories: _Category[];
  activeCategoryIds: _Category['id'][];
  disabledCategoryIds?: _Category['id'][];
  onFilterChange: (change: Record<string, boolean>) => void;
};

const InnerCategoryBox = <_Category extends Category>(props: CategoryBoxProps<_Category>) => {
  const {
    title,
    tooltipWithEmphasis,
    categoryClasses,
    categories,
    activeCategoryIds,
    disabledCategoryIds,
    onFilterChange,
  } = props;
  const uiStrings = useUiStrings();

  const unclassifiedCategories = useMemo(() => {
    const result = categories.filter(
      category => !categoryClasses.some(categoryClass => !!category.class && category.class === categoryClass.id)
    );
    return result;
  }, [categories, categoryClasses]);

  const isSingleClass = categoryClasses.length === 1;
  const isMultiClass = categoryClasses.length > 1;
  const hasUnclassifiedCategories = unclassifiedCategories.length > 0;

  return (
    <Box className="gap-y-3">
      <div className="flex flex-row items-center justify-between gap-x-2">
        <Heading3 className="text-lg">{title}</Heading3>
        {!!tooltipWithEmphasis && <PopoverTooltip tooltipWithEmphasis={tooltipWithEmphasis} />}
      </div>
      {categoryClasses.map((categoryClass, index) => {
        const filteredCategories = categories.filter(category => category.class === categoryClass.id);
        if (!categories.length) return null;
        const isLast = index === categoryClasses.length - 1;
        const className =
          (isSingleClass && hasUnclassifiedCategories) || !isLast ? 'border-b border-midground2 pb-3' : undefined;
        return (
          <CategoryBoxSection
            key={`category-class-${categoryClass.id}`}
            title={isMultiClass || hasUnclassifiedCategories ? categoryClass.title : undefined}
            pepIcon={isMultiClass || hasUnclassifiedCategories ? categoryClass.pepIcon : undefined}
            categories={filteredCategories}
            activeCategoryIds={activeCategoryIds}
            disabledCategoryIds={disabledCategoryIds}
            handleFilterChange={onFilterChange}
            className={className}
          />
        );
      })}
      {hasUnclassifiedCategories && (
        <CategoryBoxSection
          title={isSingleClass || isMultiClass ? uiStrings.general.otherCategories : undefined}
          pepIcon={isSingleClass || isMultiClass ? 'NavigationMenuHorizontal' : undefined}
          categories={unclassifiedCategories}
          activeCategoryIds={activeCategoryIds}
          disabledCategoryIds={disabledCategoryIds}
          handleFilterChange={onFilterChange}
        />
      )}
    </Box>
  );
};

export const CategoryBox = <_Category extends Category>(props: CategoryBoxProps<_Category>) => {
  if (props.categories.length === 0) return null;
  return <InnerCategoryBox {...props} />;
};
